import PlateStartFields, { EMPTY_START, startBody } from './culture/PlateStartFields';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import QRScanModal from './QRScanModal';
import { uploadPhotos } from '../utils/uploadPhotos';
import MediumOptions from './culture/MediumOptions';

/*
 * New culture plate, step by step and camera first (pre-production UX, 2026-10).
 *
 * 1. Source: a collection (POST /plates, origin field_collection: the control
 *    plate and the species plate) or an existing tube (POST /plates/from-slant:
 *    one plate, the slant recorded as its source).
 * 2. Identify it: the camera opens at once; a code or isolate name can be
 *    typed instead. Any code /lookup knows is accepted, and the source follows
 *    what was scanned (a tube's code picks "tube" even if "collection" was chosen).
 * 3. Photograph what is being transferred, and name the plate: keep the code
 *    the lineage gives it, or (from a collection) a new isolate name.
 * 4. Notes and tags, then Save.
 *
 * Tags are written into the plate's notes, where people already read them.
 * "Contaminated" also records a check with that status on each new plate, so
 * the plate shows up as contaminated in the queue, not only in its notes.
 */
export const TAGS = ['contaminated', 'new_isolation', 'transfer', 'purification'];
const STEPS = ['source', 'identify', 'capture', 'notes'];
const DEFAULT_TEMP = 25;

export default function NewCultureWizard({ protocols = [], onCreated, onCancel }) {
  const { t } = useTranslation(['culture', 'common']);
  const [step, setStep] = useState(0);
  const [sourceKind, setSourceKind] = useState(null); // 'collection' | 'tube'
  const [scanning, setScanning] = useState(false);
  const [code, setCode] = useState('');
  const [finding, setFinding] = useState(false);
  const [source, setSource] = useState(null); // { kind, id, code, taxon_name, protocol, sample_id }
  const [findError, setFindError] = useState('');
  const [suggestion, setSuggestion] = useState(null); // /taxonomy/protocol-lookup when found
  const [useSuggested, setUseSuggested] = useState(true);
  const [protocol, setProtocol] = useState('');
  const [temperature, setTemperature] = useState(DEFAULT_TEMP);
  const [start, setStart] = useState(EMPTY_START);
  const [naming, setNaming] = useState('keep'); // 'keep' | 'new'
  const [isolateName, setIsolateName] = useState('');
  const [photos, setPhotos] = useState([]); // { file, url }
  const [tags, setTags] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const headingRef = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => { headingRef.current?.focus?.(); }, [step]);
  // Object URLs for the previews are released when the wizard closes.
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL?.(p.url)), []);

  const chooseSource = (kind) => {
    setSourceKind(kind);
    setSource(null);
    setFindError('');
    setStep(1);
    setScanning(true); // camera first
  };

  const find = async (raw) => {
    const value = String(raw || '').trim();
    if (!value) return;
    setFinding(true);
    setFindError('');
    try {
      const { data } = await api.get('/lookup', { params: { code: value } });
      let found = null;
      if (data.type === 'collection') {
        found = { kind: 'collection', id: data.sample_id || data.id, code: data.code, taxon_name: data.taxon_name,
          taxonomic_rank: data.taxonomic_rank };
      } else if (data.type === 'specimen' && data.sample_id) {
        // A specimen code is its collection's qr_uid: the collection is the source.
        found = { kind: 'collection', id: data.sample_id, code: data.code, taxon_name: data.taxon_name };
      } else if (data.type === 'slant') {
        found = { kind: 'tube', id: data.slant_id || data.id, code: data.code,
          taxon_name: data.taxon_name || data.strain_species, protocol: data.protocol };
      }
      if (!found) {
        setSource(null);
        setFindError(t(data.type === 'strain' ? 'identify.strainNotTube' : 'identify.wrongKind',
          { code: data.code || value, kind: data.type }));
        return;
      }
      setSource(found);
      setSourceKind(found.kind);
      setCode('');
      setProtocol(found.protocol || '');
      setSuggestion(null);
      if (found.kind === 'collection' && found.taxon_name && found.taxonomic_rank !== 'unknown') {
        api.get('/taxonomy/protocol-lookup', { params: { taxon_name: found.taxon_name } })
          .then((res) => {
            if (!res.data?.found) return;
            setSuggestion(res.data);
            setUseSuggested(true);
            if (typeof res.data.temperature_c === 'number') setTemperature(res.data.temperature_c);
          })
          .catch(() => {});
      }
    } catch (e) {
      setSource(null);
      setFindError(e?.response?.status === 404 ? t('identify.notFound', { code: value })
        : (e?.response?.data?.error || t('identify.failed')));
    } finally {
      setFinding(false);
    }
  };

  const addPhotos = (files) => {
    const added = Array.from(files || []).filter((f) => f.type?.startsWith('image/') || !f.type)
      .map((file) => ({ file, url: URL.createObjectURL?.(file) || '' }));
    setPhotos((list) => [...list, ...added]);
  };
  const removePhoto = (i) => setPhotos((list) => {
    URL.revokeObjectURL?.(list[i].url);
    return list.filter((_, j) => j !== i);
  });
  const toggleTag = (tag) => setTags((list) => (list.includes(tag) ? list.filter((x) => x !== tag) : [...list, tag]));

  const isolateOk = naming === 'keep' || /^[A-Za-z0-9._-]{2,48}$/.test(isolateName.trim());
  const protocolOk = sourceKind === 'collection' ? (suggestion && useSuggested) || !!protocol : !!protocol;
  const canNext = [
    !!sourceKind,
    !!source,
    protocolOk && isolateOk,
    true,
  ][step];

  const composedNotes = () => {
    const tagLine = tags.length ? `${t('notes.tagsPrefix')}: ${tags.map((x) => t(`tags.${x}`)).join(', ')}` : '';
    return [tagLine, notes.trim()].filter(Boolean).join('\n') || null;
  };

  const save = async () => {
    setSaving(true);
    setError('');
    let plates;
    try {
      const temp = parseFloat(temperature);
      const temperature_c = Number.isFinite(temp) ? temp : DEFAULT_TEMP;
      if (source.kind === 'collection') {
        const body = { origin: 'field_collection', sample_id: source.id, temperature_c, notes: composedNotes(),
          accept_suggested_protocol: !!(suggestion && useSuggested), ...startBody(start) };
        if (!(suggestion && useSuggested)) body.manual_protocol = protocol;
        if (naming === 'new') body.isolate_code = isolateName.trim();
        plates = (await api.post('/plates', body)).data.plates;
      } else {
        plates = (await api.post('/plates/from-slant', { slant_id: source.id, protocol, temperature_c,
          notes: composedNotes(), ...startBody(start) })).data.plates;
      }
    } catch (e) {
      setError(e?.response?.data?.error || t('save.failed'));
      setSaving(false);
      return;
    }
    // The plates exist from here on: what follows can only add warnings.
    const warnings = [];
    if (tags.includes('contaminated')) {
      for (const p of plates) {
        try {
          await api.post(`/plates/${p.plate_id}/checks`, { status: 'contaminated', notes: t('save.contaminatedCheckNote') });
        } catch {
          warnings.push(t('save.checkFailed', { code: p.plate_code }));
        }
      }
    }
    if (photos.length) {
      const first = plates[0];
      const anchor = { plate_id: first.plate_id, ...(source.kind === 'collection' ? { sample_id: source.id } : {}) };
      try {
        const res = await uploadPhotos(photos.map(({ file }) => ({ file, photoType: 'plate_photo',
          notes: t('capture.photoNote', { code: source.code }) })), anchor, source.taxon_name || null, 'lab');
        if (res.failed.length) warnings.push(t('save.photosFailed', { count: res.failed.length, code: first.plate_code }));
      } catch {
        warnings.push(t('save.photosFailed', { count: photos.length, code: first.plate_code }));
      }
    }
    setSaving(false);
    onCreated?.({ plates, source, warnings, isolateCode: naming === 'new' ? isolateName.trim() : null });
  };

  const back = () => {
    setError('');
    if (step === 0) onCancel?.();
    else setStep(step - 1);
  };

  const protocolOptions = (!protocol || protocols.some((p) => p.name === protocol))
    ? protocols : [{ protocol_id: '__current', name: protocol }, ...protocols];

  return (
    <section style={panel} data-testid="culture-wizard" aria-labelledby="culture-wizard-title">
      <h3 id="culture-wizard-title" style={{ marginTop: 0 }}>{t('title')}</h3>
      <ol style={stepper} aria-label={t('stepsLabel')}>
        {STEPS.map((s, i) => (
          <li key={s} aria-current={i === step ? 'step' : undefined}
            style={{ ...stepPill, ...(i === step ? stepActive : i < step ? stepDone : {}) }}>
            {i + 1}. {t(`steps.${s}`)}
          </li>
        ))}
      </ol>
      <h4 ref={headingRef} tabIndex={-1} style={{ margin: '12px 0 8px' }} data-testid="culture-step-title">
        {t(`steps.${STEPS[step]}`)}
      </h4>

      {step === 0 && (
        <div style={bigChoices}>
          {['collection', 'tube'].map((kind) => (
            <button key={kind} type="button" onClick={() => chooseSource(kind)} data-testid={`culture-source-${kind}`}
              aria-pressed={sourceKind === kind} style={{ ...bigChoice, ...(sourceKind === kind ? bigChoiceOn : {}) }}>
              <strong style={{ fontSize: 17 }}>{t(`source.${kind}`)}</strong>
              <span style={muted}>{t(`source.${kind}Hint`)}</span>
            </button>
          ))}
        </div>
      )}

      {step === 1 && (
        <div>
          <p style={muted}>{t(`identify.hint_${sourceKind}`)}</p>
          <form onSubmit={(e) => { e.preventDefault(); find(code); }} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setScanning(true)} style={btn('var(--c-primary)')} data-testid="culture-camera">
              {t('identify.camera')}
            </button>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('identify.placeholder')}
              aria-label={t('identify.placeholder')} autoComplete="off" style={{ ...inputS, flex: '1 1 200px' }}
              data-testid="culture-code" />
            <button type="submit" disabled={finding || !code.trim()} style={btn('var(--c-info)')} data-testid="culture-find">
              {finding ? t('common:loading') : t('identify.find')}
            </button>
          </form>
          {findError && <p role="alert" style={errS} data-testid="culture-find-error">{findError}</p>}
          {source && (
            <div style={card} data-testid="culture-source-card">
              <span style={badgeS}>{t(`source.${source.kind}`)}</span>{' '}
              <strong>{source.code}</strong>
              {source.taxon_name && <> · <em>{source.taxon_name}</em></>}
              {source.protocol && <div style={muted}>{t('identify.medium', { protocol: source.protocol })}</div>}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <p style={{ ...muted, marginTop: 0 }}>{t('capture.hint')}</p>
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment" multiple hidden
              onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} data-testid="culture-photo-input" />
            <button type="button" onClick={() => photoInputRef.current?.click()} style={btn('var(--c-primary)')}
              data-testid="culture-photo">{photos.length ? t('capture.another') : t('capture.take')}</button>
            {photos.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {photos.map((p, i) => (
                  <figure key={p.url || i} style={{ margin: 0, position: 'relative' }}>
                    {p.url ? <img src={p.url} alt={t('capture.photoAlt', { n: i + 1 })} style={thumb} />
                      : <span style={thumb}>{p.file.name}</span>}
                    <button type="button" onClick={() => removePhoto(i)} aria-label={t('capture.remove', { n: i + 1 })}
                      style={removeBtn}>×</button>
                  </figure>
                ))}
              </div>
            )}
          </div>

          <fieldset style={fieldset}>
            <legend>{t('name.legend')}</legend>
            <label style={radioRow}>
              <input type="radio" name="culture-naming" checked={naming === 'keep'} onChange={() => setNaming('keep')} />
              <span>{t('name.keep', { code: source.code })}</span>
            </label>
            {source.kind === 'collection' ? (
              <>
                <label style={radioRow}>
                  <input type="radio" name="culture-naming" checked={naming === 'new'} onChange={() => setNaming('new')}
                    data-testid="culture-name-new" />
                  <span>{t('name.new')}</span>
                </label>
                {naming === 'new' && (
                  <>
                    <input value={isolateName} onChange={(e) => setIsolateName(e.target.value)} style={inputS}
                      placeholder={t('name.placeholder')} aria-label={t('name.new')} data-testid="culture-isolate-name"
                      aria-invalid={!isolateOk} />
                    {!isolateOk && isolateName && <p style={errS}>{t('name.invalid')}</p>}
                  </>
                )}
              </>
            ) : <p style={muted}>{t('name.tubeKeeps')}</p>}
          </fieldset>

          <fieldset style={fieldset}>
            <legend>{t('medium.legend')}</legend>
            {source.kind === 'collection' && <p style={{ ...muted, marginTop: 0 }}>{t('medium.pairNote')}</p>}
            {suggestion && source.kind === 'collection' && (
              <label style={radioRow}>
                <input type="checkbox" checked={useSuggested} onChange={(e) => setUseSuggested(e.target.checked)}
                  data-testid="culture-use-suggested" />
                <span>{t('medium.useSuggested', { protocol: suggestion.protocol })}</span>
              </label>
            )}
            {!(suggestion && useSuggested && source.kind === 'collection') && (
              <label style={labelS}>{t('medium.protocol')}
                <select value={protocol} onChange={(e) => setProtocol(e.target.value)} style={inputS}
                  data-testid="culture-protocol" aria-required="true">
                  <option value="">—</option>
                  <MediumOptions protocols={protocolOptions} />
                </select>
              </label>
            )}
            <label style={labelS}>{t('medium.temperature')}
              <input type="number" step="0.5" value={temperature} onChange={(e) => setTemperature(e.target.value)}
                style={{ ...inputS, maxWidth: 120 }} data-testid="culture-temperature" />
            </label>
            <PlateStartFields value={start} labelStyle={labelS} inputStyle={inputS} testIdPrefix="culture-start"
                              onChange={patch => setStart(s => ({ ...s, ...patch }))} />
          </fieldset>
        </div>
      )}

      {step === 3 && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div role="group" aria-label={t('notes.tagsLabel')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TAGS.map((tag) => (
              <button key={tag} type="button" aria-pressed={tags.includes(tag)} onClick={() => toggleTag(tag)}
                data-testid={`culture-tag-${tag}`}
                style={{ ...chip, ...(tags.includes(tag) ? (tag === 'contaminated' ? chipDanger : chipOn) : {}) }}>
                {tags.includes(tag) ? '✓ ' : ''}{t(`tags.${tag}`)}
              </button>
            ))}
          </div>
          {tags.includes('contaminated') && <p style={muted}>{t('notes.contaminatedHint')}</p>}
          <label style={labelS}>{t('notes.label')}
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} style={inputS}
              data-testid="culture-notes" />
          </label>
          <div style={card} data-testid="culture-summary">
            <div><strong>{t('summary.source')}:</strong> {source.code}{source.taxon_name ? ` · ${source.taxon_name}` : ''}</div>
            <div><strong>{t('summary.plates')}:</strong> {t(source.kind === 'collection' ? 'summary.pair' : 'summary.one')}</div>
            <div><strong>{t('summary.name')}:</strong> {naming === 'new' ? isolateName.trim() : t('summary.keep')}</div>
            <div><strong>{t('summary.photos')}:</strong> {photos.length}</div>
          </div>
        </div>
      )}

      {error && <p role="alert" style={errS} data-testid="culture-error">{error}</p>}

      <div style={actions}>
        <button type="button" onClick={back} disabled={saving} style={btn('var(--c-text-muted)')} data-testid="culture-back">
          {step === 0 ? t('common:cancel') : t('back')}
        </button>
        {step < STEPS.length - 1 && step > 0 && (
          <button type="button" onClick={() => setStep(step + 1)} disabled={!canNext} style={btn('var(--c-primary)')}
            data-testid="culture-next">{t('next')}</button>
        )}
        {step === STEPS.length - 1 && (
          <button type="button" onClick={save} disabled={saving} style={btn('var(--c-success)')}
            data-testid="culture-save">{saving ? t('common:saving') : t('save.button')}</button>
        )}
      </div>

      <QRScanModal open={scanning} onClose={() => setScanning(false)}
        onDetected={(scanned) => { setScanning(false); find(scanned); }} />
    </section>
  );
}

const panel = { background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 20, marginBottom: 24 };
const stepper = { display: 'flex', gap: 6, flexWrap: 'wrap', listStyle: 'none', padding: 0, margin: 0 };
const stepPill = { padding: '4px 10px', borderRadius: 999, fontSize: 12, border: '1px solid var(--c-border)', color: 'var(--c-text-muted-fg)' };
const stepActive = { background: 'var(--c-primary)', color: '#fff', borderColor: 'var(--c-primary)' };
const stepDone = { color: 'var(--c-text-fg)' };
const bigChoices = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 };
const bigChoice = { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start', textAlign: 'start',
  padding: 18, minHeight: 96, borderRadius: 10, border: '2px solid var(--c-border)', background: 'var(--c-surface)',
  color: 'var(--c-text-fg)', cursor: 'pointer' };
const bigChoiceOn = { borderColor: 'var(--c-primary)' };
const muted = { fontSize: 13, color: 'var(--c-text-muted-fg)' };
const errS = { color: 'var(--c-danger-fg)', margin: '8px 0' };
const inputS = { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--c-border)', fontSize: 15,
  background: 'var(--c-surface)', color: 'var(--c-text-fg)', width: '100%', boxSizing: 'border-box' };
const labelS = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 };
const card = { marginTop: 10, padding: 12, borderRadius: 8, border: '1px solid var(--c-border)', background: 'var(--c-surface)', fontSize: 14 };
const badgeS = { fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--c-primary)', color: '#fff' };
const thumb = { display: 'block', width: 96, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border)' };
const removeBtn = { position: 'absolute', top: 2, insetInlineEnd: 2, width: 24, height: 24, borderRadius: 12,
  border: 'none', background: 'rgba(0,0,0,.6)', color: '#fff', cursor: 'pointer' };
const fieldset = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 12, display: 'grid', gap: 8 };
const radioRow = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 };
const chip = { padding: '8px 14px', borderRadius: 999, border: '1px solid var(--c-border)', background: 'var(--c-surface)',
  color: 'var(--c-text-fg)', cursor: 'pointer', fontSize: 14, minHeight: 40 };
const chipOn = { background: 'var(--c-primary)', color: '#fff', borderColor: 'var(--c-primary)' };
const chipDanger = { background: 'var(--c-danger-fg)', color: '#fff', borderColor: 'var(--c-danger-fg)' };
const actions = { display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-start', flexWrap: 'wrap' };
const btn = (bg) => ({ padding: '10px 18px', minHeight: 44, borderRadius: 6, border: 'none', background: bg,
  color: '#fff', fontSize: 15, cursor: 'pointer' });
